import {
  type MutableRefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import Loader from "common/components/Loader";

import { useActiveConversationContext } from "../../context/ActiveConversationContext";
import useActiveConversationState from "../../hooks/useActiveConversationState";
import { formatConversationMessagesDate } from "../../utils";
import Message from "./Message/Message";
import Notification from "./Notification";
import TypingIndicator from "./TypingIndicator";

import type { InfiniteData } from "@tanstack/query-core";
import type { ConversationMessage } from "../../types";

interface MessageThreadProps {
  setEditMessage: (message: ConversationMessage) => void;
  messages: InfiniteData<ConversationMessage[], object>;
  loadMoreMessages: (param: "next" | "prev") => Promise<void>;
  isLoadingMessages: { next: boolean; prev: boolean; all: boolean };
  hasMoreMessages: { next: boolean; prev: boolean };
  showLeadPreview: (leadId: string) => void;
  shouldScrollToMessageRef: MutableRefObject<boolean>;
}

export default function MessageThread({
  setEditMessage,
  messages,
  isLoadingMessages,
  loadMoreMessages,
  hasMoreMessages,
  showLeadPreview,
  shouldScrollToMessageRef,
}: MessageThreadProps) {
  const messageThreadRef = useRef<HTMLDivElement>();

  const { activeConversation } = useActiveConversationContext();
  const { activeConversationState } = useActiveConversationState();
  const [topIntersectionElement, setTopIntersectionElement] =
    useState<HTMLDivElement>();
  const [bottomIntersectionElement, setBottomIntersectionElement] =
    useState<HTMLDivElement>();

  const isMessageSearchResult =
    activeConversation?.highlight && "body" in activeConversation.highlight;

  useEffect(() => {
    let topIntersectionObserver: IntersectionObserver;
    let bottomIntersectionObserver: IntersectionObserver;

    if (topIntersectionElement) {
      topIntersectionObserver = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            !isLoadingMessages.next &&
            hasMoreMessages.next
          ) {
            loadMoreMessages("next");
          }
        },
        { root: messageThreadRef.current },
      );
      topIntersectionObserver.observe(topIntersectionElement);
    }
    if (bottomIntersectionElement) {
      bottomIntersectionObserver = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            !isLoadingMessages.prev &&
            hasMoreMessages.prev
          ) {
            loadMoreMessages("prev");
          }
        },
        { root: messageThreadRef.current },
      );
      bottomIntersectionObserver.observe(bottomIntersectionElement);
    }

    return () => {
      topIntersectionObserver?.disconnect();
      bottomIntersectionObserver?.disconnect();
    };
  }, [
    topIntersectionElement,
    bottomIntersectionElement,
    hasMoreMessages,
    isLoadingMessages,
  ]);

  const allMessages = messages ? messages.pages.flat() : [];
  const messagesComponents = [];

  const isInitialLoading = isMessageSearchResult
    ? !messages || !("after" in messages.pageParams[0])
    : isLoadingMessages.all;

  if (allMessages) {
    for (let i = 0; i < allMessages.length; i += 1) {
      const message = allMessages[i];
      const nextMessage = allMessages[i + 1];
      const date = new Date(message.created_at);
      const nextDate = nextMessage && new Date(nextMessage.created_at);

      const isDifferentDate =
        !nextDate ||
        date.getDate() !== nextDate.getDate() ||
        date.getMonth() !== nextDate.getMonth();
      const isDifferentSender =
        !nextMessage || nextMessage.sender.id !== message.sender.id;

      let component = (
        <Message
          key={message.urn + message.created_at}
          message={message}
          isFirstMessage={isDifferentSender || isDifferentDate}
          setEditMessage={setEditMessage}
          showLeadPreview={showLeadPreview}
        />
      );
      if (message.urn === activeConversation?.last_message?.urn) {
        component = <div id="highlighted-message">{component}</div>;
      }
      if (i === allMessages.length - 20) {
        messagesComponents.push(
          <div ref={setTopIntersectionElement}>{component}</div>,
        );
      } else if (i === (isMessageSearchResult ? 20 : 0)) {
        messagesComponents.push(
          <div ref={setBottomIntersectionElement}>{component}</div>,
        );
      } else {
        messagesComponents.push(component);
      }

      if (isDifferentDate) {
        const formattedDate = formatConversationMessagesDate(date);

        messagesComponents.push(
          <div
            className="my-2.5 flex flex-row items-center justify-center gap-3"
            key={formattedDate}
          >
            <div className="h-px w-full bg-black-200" />

            <span className="text-nowrap text-button-xs text-black-700 first-letter:capitalize">
              {formattedDate}
            </span>

            <div className="h-px w-full bg-black-200" />
          </div>,
        );
      }
    }

    if (activeConversation && !hasMoreMessages.next) {
      messagesComponents.push(
        <Notification
          title={`This is the beginning of your conversation with ${activeConversation.participants[0].full_name}`}
          variant="gray"
        />,
      );
    }
    const loaderComponent = (
      <div className="mx-auto font-medium text-xs-bold">
        Loading messages...
      </div>
    );
    if (isLoadingMessages.next) {
      messagesComponents.push(loaderComponent);
    } else if (isLoadingMessages.prev && isMessageSearchResult) {
      messagesComponents.unshift(loaderComponent);
    }
  }

  useEffect(() => {
    //when the conversation changes we should scroll to highlighted/most recent message
    shouldScrollToMessageRef.current = true;
  }, [activeConversationState]);

  useEffect(() => {
    if (messages?.pages) {
      if (shouldScrollToMessageRef.current && !isInitialLoading) {
        //scroll to the highlighted message
        if (isMessageSearchResult && "after" in messages.pageParams[0]) {
          requestAnimationFrame(() => {
            const element = document.getElementById("highlighted-message");
            if (element) {
              element.scrollIntoView({
                block: "center",
              });
            }
          });
          //scroll to end
        } else {
          messageThreadRef.current.scrollTo(
            0,
            messageThreadRef.current.scrollHeight,
          );
        }
        shouldScrollToMessageRef.current = false;
      }
    }
  }, [messages, isMessageSearchResult, isInitialLoading]);

  useLayoutEffect(() => {
    if (
      messages &&
      isMessageSearchResult &&
      "before" in messages.pageParams[0] &&
      hasMoreMessages.prev &&
      !isLoadingMessages.prev
    ) {
      loadMoreMessages("prev");
    }
  }, [messages, isLoadingMessages, hasMoreMessages]);

  return (
    <div
      className="relative flex flex-1 flex-col-reverse overflow-y-auto p-4 scrollbar-thin"
      ref={messageThreadRef}
    >
      {isInitialLoading ? (
        <Loader />
      ) : (
        <>
          <TypingIndicator />
          {messagesComponents}
        </>
      )}
    </div>
  );
}
